import { useEffect, useId, useImperativeHandle, useRef, useState, type Ref } from 'react';
import type { PrizeColor, Segment } from '../../shared/types';
import { ServiceClubMark } from './ServiceClubMark';
import './Wheel3D.css';

const R = 96;
/**
 * Distancia a la que empieza el texto de un gajo. El hub mide 33% del disco
 * (radio 33) y con su aro de sombra se come hasta ~35: centrar el texto en el
 * gajo metia las lineas largas por debajo. Anclado por el borde interior,
 * ninguna linea puede invadirlo, mida lo que mida.
 */
const TEXT_INNER = 41;
const TURNS = 5;
const SPIN_MS = 4600;
const REDUCED_MS = 700;
const RING_LIGHTS = 20;

const FILL: Record<PrizeColor, string> = {
  sky: 'var(--seg-sky)',
  mint: 'var(--seg-mint)',
  peach: 'var(--seg-peach)',
  lilac: 'var(--seg-lilac)',
  sand: 'var(--seg-sand)',
  coral: 'var(--seg-coral)',
};

/** Los gajos claros llevan texto navy; el coral, blanco. */
const INK: Record<PrizeColor, string> = {
  sky: 'var(--navy)',
  mint: 'var(--navy)',
  peach: 'var(--navy)',
  lilac: 'var(--navy)',
  sand: 'var(--navy)',
  coral: '#ffffff',
};

function polar(angleDeg: number, radius: number): [number, number] {
  const rad = ((angleDeg - 90) * Math.PI) / 180;
  return [radius * Math.cos(rad), radius * Math.sin(rad)];
}

function sectorPath(from: number, to: number): string {
  const [x0, y0] = polar(from, R);
  const [x1, y1] = polar(to, R);
  const largeArc = to - from > 180 ? 1 : 0;
  return `M 0 0 L ${x0.toFixed(2)} ${y0.toFixed(2)} A ${R} ${R} 0 ${largeArc} 1 ${x1.toFixed(2)} ${y1.toFixed(2)} Z`;
}

/**
 * Parte el extra en lineas cortas para que quepa en el gajo. Si no cabe, lo
 * corta con puntos suspensivos en vez de perder palabras por el camino.
 */
export function wrap(text: string, maxChars: number, maxLines: number): string[] {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [];

  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const candidate = line ? `${line} ${word}` : word;
    if (candidate.length > maxChars && line) {
      lines.push(line);
      line = word;
    } else {
      line = candidate;
    }
  }
  if (line) lines.push(line);

  if (lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  const last = kept[maxLines - 1]!;
  kept[maxLines - 1] = `${last.slice(0, Math.max(1, maxChars - 1)).trimEnd()}…`;
  return kept;
}

export interface WheelHandle {
  /** Gira hasta el gajo indicado. Resuelve cuando la animacion termina. */
  spinTo(index: number): Promise<void>;
}

interface Props {
  segments: Segment[];
  disabled?: boolean;
  hubLabel?: string;
  onSpin: () => void;
  ref?: Ref<WheelHandle>;
}

export function Wheel3D({ segments, disabled = false, hubLabel = 'SPIN', onSpin, ref }: Props) {
  const gradientId = useId();
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const faceRef = useRef<HTMLDivElement>(null);
  const resolveRef = useRef<(() => void) | null>(null);

  const count = Math.max(segments.length, 1);
  const step = 360 / count;

  const reduced =
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const duration = reduced ? REDUCED_MS : SPIN_MS;

  useImperativeHandle(ref, () => ({
    spinTo(index: number) {
      return new Promise<void>((resolve) => {
        // Angulo que deja el centro del gajo bajo la aguja (12 en punto).
        const target = ((-(index * step + step / 2) % 360) + 360) % 360;
        // Un poco de ruido para que no pare siempre clavado en el centro.
        const jitter = (Math.random() - 0.5) * step * 0.55;
        setRotation((current) => {
          const currentMod = ((current % 360) + 360) % 360;
          let delta = target - currentMod;
          if (delta < 0) delta += 360;
          return current + 360 * (reduced ? 1 : TURNS) + delta + jitter;
        });
        setSpinning(true);
        resolveRef.current = resolve;
      });
    },
  }));

  // La animacion la lleva una transicion CSS; el fin se detecta en el elemento.
  useEffect(() => {
    const node = faceRef.current;
    if (!node) return;
    const done = (event: TransitionEvent) => {
      if (event.target !== node || event.propertyName !== 'transform') return;
      setSpinning(false);
      resolveRef.current?.();
      resolveRef.current = null;
    };
    node.addEventListener('transitionend', done);
    return () => node.removeEventListener('transitionend', done);
  }, []);

  // Toda la ruleta es el disparador: el hub sigue siendo el boton real (foco y
  // teclado), pero el click se escucha en el escenario, asi que vale pinchar
  // cualquier punto del disco, el aro o la aguja. El boton no lleva onClick
  // para no girar dos veces cuando su click burbujea hasta aqui.
  const locked = disabled || spinning;
  const handleClick = () => {
    if (locked) return;
    onSpin();
  };

  return (
    <div
      className={`wheel-stage${spinning ? ' is-spinning' : ''}${locked ? ' is-locked' : ''}`}
      onClick={handleClick}
    >
      <div className="wheel-shadow" />

      <div className="wheel-3d">
        <div className="wheel-side-deep" />
        <div className="wheel-side" />

        <div
          ref={faceRef}
          className="wheel-face"
          style={{
            transform: `rotate(${rotation}deg)`,
            transition: `transform ${duration}ms cubic-bezier(0.15, 0.9, 0.2, 1)`,
          }}
        >
          <svg viewBox="-100 -100 200 200" role="img" aria-label="Prize wheel: every slice wins free fuel">
            <defs>
              <radialGradient id={`${gradientId}-shade`} cx="34%" cy="26%" r="78%">
                <stop offset="0%" stopColor="#fff" stopOpacity="0.5" />
                <stop offset="55%" stopColor="#fff" stopOpacity="0" />
                <stop offset="100%" stopColor="#000" stopOpacity="0.22" />
              </radialGradient>
            </defs>

            {segments.map((segment, i) => {
              const from = i * step;
              const mid = from + step / 2;
              const flip = mid > 180;
              const angle = flip ? mid + 90 : mid - 90;
              // El texto corto manda sobre el largo: es el que cabe girando.
              const fuerte = segment.corto.length > 0;
              // Lo que cabe entre el hub y el aro: ~50 unidades. En negrita
              // (8px) entran unos 9 caracteres por linea; en fino (6,4px), 11.
              const lines = fuerte
                ? wrap(segment.corto, 9, 2)
                : wrap(segment.extra, 11, 3);
              const blockTop = -((lines.length + 1) * 8.4) / 2 + 6.6;

              return (
                <g key={segment.index}>
                  <path className="seg-path" d={sectorPath(from, from + step)} fill={FILL[segment.color]} />
                  <g transform={`rotate(${angle}) translate(${flip ? -TEXT_INNER : TEXT_INNER} 0)`}>
                    <text textAnchor={flip ? 'end' : 'start'} fill={INK[segment.color]}>
                      <tspan className="seg-title" x="0" dy={blockTop}>
                        FREE FUEL
                      </tspan>
                      {lines.map((line, k) => (
                        <tspan className={fuerte ? 'seg-strong' : 'seg-extra'} key={k} x="0" dy="8.4">
                          {line}
                        </tspan>
                      ))}
                    </text>
                  </g>
                </g>
              );
            })}

            {/* Sombreado global sobre los gajos: da curvatura al disco. */}
            <circle r={R} fill={`url(#${gradientId}-shade)`} pointerEvents="none" />
            <circle r={R} fill="none" stroke="var(--navy)" strokeWidth="3" />
          </svg>
        </div>

        <div className="wheel-ring">
          <svg viewBox="-100 -100 200 200" aria-hidden="true">
            <defs>
              <linearGradient id="ringGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--edge-top)" />
                <stop offset="50%" stopColor="var(--edge-mid)" />
                <stop offset="100%" stopColor="var(--edge-low)" />
              </linearGradient>
            </defs>
            <circle className="ring-body" r="96" />
            {Array.from({ length: RING_LIGHTS }, (_, i) => {
              const [x, y] = polar((360 / RING_LIGHTS) * i, 96);
              return (
                <circle
                  className="ring-light"
                  key={i}
                  cx={x}
                  cy={y}
                  r="1.9"
                  style={{ animationDelay: `${(i % 4) * 0.15}s` }}
                />
              );
            })}
          </svg>
        </div>

        <div className="wheel-gloss" />

        <button className="wheel-hub" disabled={locked}>
          <span className="hub-word">{spinning ? '…' : hubLabel}</span>
          <ServiceClubMark className="hub-mark" />
        </button>
      </div>

      <div className="wheel-pointer">
        <svg viewBox="0 0 40 62" aria-hidden="true">
          <path d="M20 62 L2 16 A20 20 0 1 1 38 16 Z" fill="var(--red)" />
          <path d="M20 62 L20 4 A20 20 0 0 1 38 16 Z" fill="var(--red-hover)" />
          <circle cx="20" cy="18" r="6" fill="#fff" opacity="0.9" />
        </svg>
      </div>
    </div>
  );
}
